// sensei-app/client/ui/StudentCertificatesView.js
import { ComponentBase } from "../../core/ComponentBase.js";
import { RpcDataProvider } from "../../core/RpcDataProvider.js";

export class StudentCertificatesView extends ComponentBase {
    constructor(config) {
        super(config);
        this.dataProvider = null;
        this.dataView = null;
    }

    async initialize() {
        // First, create the data provider that will fetch data from the server.
        this.dataProvider = new RpcDataProvider({
            appClient: this.appClient,
            eventGroup: "studentCertificatesProvider",
            rpcClass: "CertificateRpc",
            autoLoad: false, // We will load data after login to ensure context is ready.
            pageSize: 10,
        });
        await this.dataProvider.initialize();

        // Now, create the DataView and pass the provider instance to it.
        this.dataView = await this.createManagedComponent("DataView", null, {
            eventGroup: "studentCertificates",
            dataProvider: this.dataProvider, // Correctly passing the provider instance.
            title: "My Certificates",
            viewMode: 'card',
            autoLoad: false, // The view itself doesn't need to auto-load; the provider handles it.
            fields: [
                { name: 'courseName', label: 'Course', render: val => `<h5>${val}</h5>` },
                { name: 'issueDate', label: 'Date Issued', render: val => new Date(val).toLocaleDateString() },
                { name: 'certificateUid', label: 'Certificate ID', render: val => `<code class="small">${val}</code>` }
            ],
            actions: [
                // --- ADDED RE-GENERATE BUTTON ---
                {
                    label: 'Re-generate PDF',
                    icon: 'bi-arrow-repeat',
                    className: 'btn-outline-secondary btn-sm',
                    eventName: 'regenerateCertificate'
                },
                // --- END ADD ---
                {
                    label: 'Download PDF',
                    icon: 'bi-cloud-download-fill',
                    className: 'btn-primary btn-sm',
                    eventName: 'downloadCertificate'
                }
            ],
        });

        this.dataView.on('downloadCertificate', async (item) => {
            if (item && item._id) {
                try {
                    const downloadUrl = await this.appClient.rpcClient.call('CertificateRpc.getDownloadUrl', { certificateId: item._id });
                    if (downloadUrl) {
                        // Open the URL in a new tab to trigger download
                        window.open(downloadUrl, '_blank');
                    }
                } catch (error) {
                    this.appClient.notifier.error("Could not get download link for certificate.");
                    this.log.error("Failed to get certificate download URL:", error);
                }
            }
        });
        
        // --- ADDED EVENT LISTENER ---
        this.dataView.on('regenerateCertificate', async (item) => {
            if (item && item._id) {
                const confirmed = await this.appClient.notifier.confirm(
                    this.t("This will re-generate the certificate PDF with the latest information. Continue?"),
                    { title: this.t("Re-generate Certificate?"), type: 'info' }
                );
                if (confirmed) {
                    try {
                        await this.appClient.rpcClient.call('CertificateRpc.regenerateCertificate', { certificateId: item._id });
                        this.appClient.notifier.success("Certificate PDF has been updated successfully. You can now download the new version.");
                    } catch (error) {
                        this.appClient.notifier.error("Could not re-generate the certificate PDF.");
                        this.log.error("Failed to regenerate certificate:", error);
                    }
                }
            }
        });
        // --- END ADD ---

        await super.initialize();
    }

    async initAfterLogin() {
        // Now that the user is logged in, we can safely load the data.
        await this.dataProvider.loadData();
    }

    async render() {
        if (!this.container) {
            this.container = document.createElement('div');
            this.container.className = 'student-certificates-container h-100';
        }
        this.container.innerHTML = '';
        if (this.dataView) {
            this.container.appendChild(await this.dataView.render());
        }
        return this.container;
    }

    destroy() {
        this.dataView?.destroy();
        this.dataProvider?.destroy(); // Also destroy the provider to clean up its listeners.
        this.dataView = null;
        this.dataProvider = null;
        super.destroy();
    }
}